import {db, employeeDb, LocalEvent, LocalStatus} from '@src/db.ts';
import {v4 as uuid} from 'uuid';
import {
    Employee,
    EmployeeInline,
    WorkdayWithEmployee,
    WorkEventOutput
} from "@src/httpClient/__generated__/Api.ts";
import {getCurrentStartDateIso} from "@src/utils/formatDate.ts";
import {EmployeeResponse} from "@src/Store/employeesStore.ts";
export interface EventsResponse {
    results: WorkEventOutput[],
    count: number,
    __source: "online" | "offline"
}

const isoDay = (d?: string) => d ?? new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

export const addEventOffline = async (e: Omit<WorkEventOutput, 'id'>, employee: EmployeeInline, localStatus: LocalStatus = LocalStatus.pending) => {
    const temp_id = uuid();
    //@ts-ignore
    const local: LocalEvent = {...e, temp_id, localStatus: localStatus, employee: employee};
    await db.transaction('rw', db.events, async () => {
        await db.events.add(local);
    });
}

export const addEventToOutbox = async (idempotencyKey: string) => {
    const local: LocalEvent | undefined = await db.events.where("idempotency_key").equals(idempotencyKey).first()
    if (!local) return
    await db.outbox.add({
        entity: 'event',
        payload: local,
        idempotencyKey: uuid(),
        createdAt: Date.now(),
        tryCount: 0
    });
}

export async function listOfflineEvents(search: string, employeeId?: number): Promise<EventsResponse> {
    const params = new URLSearchParams(search);
    const after = params.get("record_range_after") ?? getCurrentStartDateIso();
    const before = params.get("record_range_before");
    let rows: LocalEvent[];
    if (after && before) {
        rows = await db.events
            .where("recorded_at")
            .between(after, before, true, true)
            .toArray()
    } else if (after) {
        rows = await db.events.where("recorded_at").aboveOrEqual(after).sortBy("recorded_at")
    } else if (before) {
        rows = await db.events.where("recorded_at").belowOrEqual(before).sortBy("recorded_at")
    } else {
        rows = await db.events.orderBy("recorded_at").toArray();
    }
    if (employeeId) {
        rows = employeeId
            ? rows.filter(ev => ev.employee?.id === employeeId)
            : rows;
    }

    return {
        count: rows.length,
        // @ts-ignore
        results: rows as WorkEventOutput[],
        __source: 'offline' as const
    };
}


export const syncEmployee = async (employees: Employee[]) => {
    await employeeDb.transaction('rw', employeeDb.employee, async () => {
        for (const employee of employees) {
            const exists = await employeeDb.employee.where("id").equals(employee.id).first();
            if (!exists) await employeeDb.employee.add(employee)
        }
    })
}


export const listEmployees = async (searchStr?: string): Promise<EmployeeResponse> => {
    const params = new URLSearchParams(searchStr);
    const branchId = params.get("branch_id");
    const employeeId = params.get("employee_id");
    const employeeSearchName = params.get("search");
    let rows = branchId
        ? await employeeDb.employee.where("branches_ids").equals(Number(branchId)).sortBy("last_name_kana")
        : await employeeDb.employee.orderBy("last_name_kana").toArray()

    if (employeeId) {
        rows = rows.filter(e => e.id === parseInt(employeeId))
    }

    const searchFunction = (e: Employee, search: string) => {
        if (
            e?.first_name?.includes(search) ||
            e?.last_name?.includes(search) ||
            `${e?.first_name}${e?.last_name}`.includes(search) ||
            `${e?.last_name}${e?.first_name}`.includes(search)
        ) return e
        return undefined
    }

    if (employeeSearchName) {
        rows = rows.filter(e => searchFunction(e, employeeSearchName))
    }

    const sorting = (a: Employee, b: Employee) => {
        const lastCmp = a?.last_name_kana?.localeCompare(b?.last_name_kana ?? "") ?? 0;
        if (lastCmp !== 0) return lastCmp;
        return (a?.first_name_kana?.localeCompare(b?.first_name_kana ?? "") ?? 0);
    }

    return {
        count: rows.length,
        results: rows.sort((a, b) => sorting(a, b)),
        __source: 'offline' as const
    };
}

export const syncWorkdays = async (workDays: WorkdayWithEmployee[]) => {
    for (const workDay of workDays) {
        const exists = await employeeDb.workDays.where("id").equals(workDay.id).first();
        const wd = {...workDay, employeeId: workDay?.employee?.id};
        if (!exists) await employeeDb.workDays.add(wd)
    }
}

export const listWorkDays = async (search?: string) => {
    const params = new URLSearchParams(search);
    const fromDate = isoDay(params.get("record_range_after") ?? undefined);
    const toDate = params.get("record_range_before");
    if (fromDate && toDate) {
        return employeeDb.workDays
            .where('date')
            .between(fromDate, toDate, true, true)
            .toArray();
    } else if (fromDate) {
        return employeeDb.workDays
            .where('date')
            .aboveOrEqual(fromDate)
            .toArray();
    } else if (toDate) {
        return employeeDb.workDays
            .where('date')
            .belowOrEqual(toDate)
            .toArray();
    } else {
        return employeeDb.workDays.toArray();
    }
};
